import { z } from 'zod';
import { emailSchema } from './schemas';
import { PERMISSIONS, type Permission } from './permissions';

/** Schemas da Fase 3: equipe, funções, setores, impressoras e produção. */

export const permissionSchema = z.string().refine((p): p is Permission => p in PERMISSIONS, 'Permissão desconhecida');

export const inviteStaffSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome').max(120),
  email: emailSchema,
  roleId: z.uuid('Escolha a função'),
});

export const updateMemberSchema = z
  .object({ roleId: z.uuid().optional(), status: z.enum(['ACTIVE', 'BLOCKED']).optional() })
  .refine((v) => v.roleId || v.status, 'Nada para alterar');

export const roleInputSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome').max(40),
  permissions: z.array(permissionSchema).max(100),
});

export const STATION_MODES = ['PRINTER', 'SCREEN', 'BOTH'] as const;
export type StationMode = (typeof STATION_MODES)[number];
export const STATION_MODE_LABEL: Record<StationMode, string> = { PRINTER: 'Só impressora', SCREEN: 'Só tela', BOTH: 'Tela + impressora' };

export const stationInputSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome').max(40),
  kind: z.enum(['KITCHEN', 'BAR', 'OTHER']),
  defaultPrepMinutes: z.number().int().min(1).max(240),
  /** Como o setor recebe os pedidos (omitido = mantém; setor novo nasce só com impressora). */
  mode: z.enum(STATION_MODES).optional(),
});

export const printerInputSchema = z
  .object({
    name: z.string().trim().min(2, 'Informe o nome').max(60),
    stationId: z.uuid().nullable(),
    agentId: z.uuid().nullable(),
    connection: z.enum(['NETWORK', 'WINDOWS', 'DEVICE']),
    address: z.string().trim().min(1, 'Informe o endereço').max(200),
    columns: z.union([z.literal(32), z.literal(42), z.literal(48)]),
    encoding: z.enum(['cp860', 'ascii']),
    copies: z.number().int().min(1).max(3),
    active: z.boolean(),
  })
  .refine((p) => p.connection !== 'NETWORK' || /^[\w.-]+(:\d{2,5})?$/.test(p.address), { message: 'Use IP ou nome, com porta opcional. Ex.: 192.168.0.50:9100', path: ['address'] });

export const agentCreateSchema = z.object({ name: z.string().trim().min(2, 'Informe um nome').max(60) });

export const ticketStatusSchema = z.object({ status: z.enum(['RECEIVED', 'IN_PREPARATION', 'READY', 'DELIVERED']) });

export const STATION_KIND_LABEL = { KITCHEN: 'Cozinha', BAR: 'Bar', OTHER: 'Outro' } as const;

/** Permissão necessária para operar a tela de um setor. */
export function stationPermission(kind: 'KITCHEN' | 'BAR' | 'OTHER'): Permission {
  return kind === 'BAR' ? 'production.bar' : 'production.kitchen';
}

/** Grupos de permissões para a tela de funções. */
export const PERMISSION_GROUPS: { label: string; prefix: string[] }[] = [
  { label: 'Estabelecimento', prefix: ['restaurant.', 'staff.', 'roles.', 'audit.'] },
  { label: 'Cardápio', prefix: ['menu.'] },
  { label: 'Salão', prefix: ['tables.', 'sessions.'] },
  { label: 'Pedidos e produção', prefix: ['orders.', 'production.', 'printing.'] },
  { label: 'Financeiro', prefix: ['accounts.', 'payments.', 'discounts.', 'account.', 'exit.'] },
  { label: 'Relatórios', prefix: ['dashboard.', 'reports.'] },
];
